import React, { Dispatch, SetStateAction, useCallback } from "react";
import {
  Permission,
  RemoveAccessData,
  ResourceType,
} from "components/shareSection/entities";
import { UserModelRole, SharedAccess } from "target_wasm";
import {
  fetchModelSharedWithList,
  handleModelRevokeAccess,
  handleShareModel,
} from "components/main/utils/models/ModelServices";
import { resendInvitation } from "@/lib/api/resendInvitation";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { ManageAccessDialog } from "components/shareSection/ManageAccessDialog";
import { Callout } from "components/common/Callout";
import { useSharedAccessManager } from "components/shareSection/useSharedAccessManager";
import { useModelSpaceAccess } from "components/shareSection/useModelSpaceAccess";

interface ShareModelProps {
  modelID: string;
  modelName: string;
  role: UserModelRole | undefined;
  sharedWith: SharedAccess | undefined;
  setIsShare: () => void;
  canManageAccess: boolean;
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>;
  onAfterRevoke?: (data: RemoveAccessData | null) => Promise<void>;
  /// From the model's permissions once loaded, else from its space.
  isSealed?: boolean;
}

export const ShareModel: React.FC<ShareModelProps> = (props) => {
  const userCurrentEmail = localStorage.getItem("email");
  const clientApp = useBoundStore((state) => state.clientApp);
  const bumpDetailVersion = useBoundStore((state) => state.bumpDetailVersion);

  const refreshModelPermissions = useCallback(() => {
    clientApp?.fetch_model_permissions();
  }, [clientApp]);

  const manager = useSharedAccessManager({
    entityId: props.modelID,
    setSharedWithList: props.setSharedWithList,
    fetchFn: fetchModelSharedWithList,
    onPermissionError: async () => {
      // Trigger re-render to re-read model permissions from WASM cache
      bumpDetailVersion();
    },
    onAccessChanged: refreshModelPermissions,
  });
  const { space, sealed } = useModelSpaceAccess(props.modelID);
  // Sharing and resending mint access, which a sealed space refuses even its
  // owners (`can_grant_access`); changing a role and revoking stay theirs.
  const canShare = props.canManageAccess && !(props.isSealed ?? sealed);

  const handleShareModelAction = useCallback(
    async (
      emails: string[],
      roleOrPermission: UserModelRole | Permission | null,
    ) => {
      await handleShareModel(
        props.modelName,
        emails,
        roleOrPermission as UserModelRole,
        props.modelID as string,
        () => {},
        () => {},
        props.setSharedWithList,
        String(userCurrentEmail),
      );
    },
    [props.modelName, props.modelID, props.setSharedWithList, userCurrentEmail],
  );

  const handleRevokeAccessModel = useCallback(
    async (data: RemoveAccessData | null) => {
      await handleModelRevokeAccess(
        props.modelID,
        data,
        props.setSharedWithList,
        props.modelName,
      );
      await props.onAfterRevoke?.(data);
    },
    [
      props.modelID,
      props.modelName,
      props.setSharedWithList,
      props.onAfterRevoke,
    ],
  );

  const handleResendInvitation = useCallback(
    async (email: string) => {
      await resendInvitation("model", props.modelID, props.modelName, email);
    },
    [props.modelID, props.modelName],
  );

  return (
    <ManageAccessDialog
      open={true}
      onClose={props.setIsShare}
      entityId={props.modelID}
      resourceType={ResourceType.Model}
      sharedWithList={props.sharedWith}
      setSharedWithList={props.setSharedWithList}
      canShare={canShare}
      canRevokeAccess={props.canManageAccess}
      onShare={handleShareModelAction}
      onRevokeAccess={handleRevokeAccessModel}
      onSuccess={manager.handleSuccess}
      onError={manager.handleError}
      defaultRole={UserModelRole.Collaborator}
      onRefetchSharedList={manager.handleRefetchSharedList}
      userContinuationToken={manager.userContinuationToken}
      onResendInvitation={canShare ? handleResendInvitation : undefined}
      space={space}
      viewerRole={
        props.role !== undefined && props.role !== UserModelRole.None
          ? UserModelRole[props.role]
          : undefined
      }
      loadFailed={manager.loadFailed}
      onRetryLoad={manager.retryLoad}
      notice={
        <Callout variant="info">
          People you share with see its tables, and import released versions or
          request access.
        </Callout>
      }
    />
  );
};
